export function format(first?: string, middle?: string, last?: string): string {
    return (first || "") + (middle ? ` ${middle}` : "") + (last ? ` ${last}` : "");
}

/**
 * Parses chat messages for links in the format [Text|URL] and converts them to HTML anchor tags
 *
 * @param message - The message text to parse
 * @returns The message with links converted to HTML anchor tags
 *
 * @example
 * // Returns: 'Check our <a href="https://example.com" target="_blank" rel="noopener noreferrer">privacy policy</a>'
 * parseLinks('Check our [privacy policy|https://example.com]')
 */
export function parseLinks(message: string): string {
    if (!message) {
        return "";
    }

    // Regex pattern to match [Text|URL] format
    const linkPattern = /\[(.*?)\|(https?:\/\/[^\]\s]+)\]/g;

    return message.replace(linkPattern, (_match, text, url) => {
        const trimmedText = text.trim();
        const trimmedUrl = url.trim();

        return `<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer">${trimmedText}</a>`;
    });
}

/**
 * Debug logging utility that only logs when the component has debug=true
 *
 * @param component - The component instance with a debug property
 * @param message - The message to log
 * @param data - Optional data to log along with the message
 *
 * @example
 * // In a component with @Prop() debug = false;
 * debug.log(this, "Component loaded"); // Nothing will be logged
 *
 * // In a component with @Prop() debug = true;
 * debug.log(this, "Component loaded"); // Will log: "[ComponentName] Component loaded"
 * debug.log(this, "Data received", { id: 123 }); // Will log: "[ComponentName] Data received", { id: 123 }
 */
export const debug = {
    log: (component: { debug?: boolean; constructor?: { name?: string } }, message: string, ...data: unknown[]) => {
        if (component?.debug) {
            const componentName = component.constructor?.name || "UnknownComponent";
            if (data.length > 0) {
                console.log(`[${componentName}] ${message}`, ...data);
            } else {
                console.log(`[${componentName}] ${message}`);
            }
        }
    }
};

/**
 * Scrolls a container element to the bottom
 * Useful for chat interfaces to keep the newest messages visible
 *
 * @param element - Parent element to search from (usually a component reference)
 * @param containerSelector - CSS selector for the container to scroll (default: "#chat-container")
 *
 * @example
 * // In a component:
 * scrollElementToBottom(this.chatRef);
 */
export const scrollElementToBottom = (
    element: HTMLElement | null | undefined,
    containerSelector: string = "#chat-container"
): void => {
    if (!element) {
        return;
    }

    setTimeout(() => {
        let container = null;

        container = element.shadowRoot?.querySelector(containerSelector) || element.querySelector(containerSelector);

        if (!container && element.shadowRoot) {
            const childComponents = element.shadowRoot.querySelectorAll("*");
            for (const child of childComponents) {
                if (child.shadowRoot) {
                    const found = child.shadowRoot.querySelector(containerSelector);
                    if (found) {
                        container = found;
                        break;
                    }
                }
            }
        }

        if (!container) {
            const containers = document.querySelectorAll(containerSelector);
            if (containers.length > 0) {
                container = containers[0];
            }
        }

        if (container) {
            container.scrollTop = Number.MAX_SAFE_INTEGER;

            setTimeout(() => {
                if (container) {
                    container.scrollTop = Number.MAX_SAFE_INTEGER;
                }
            }, 100);
        }
    }, 200);
};
